import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { ActionItem, ActionItemStyle, OpenDirection } from '@stylehaven/components';

import { ReduceVisibleActionItems } from '../../../helpers';

@Component({
    standalone: false,
    selector: 'digihaven-action-bar',
    templateUrl: './action-bar.component.html',
    styleUrls: ['./action-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DigihavenActionBarComponent implements OnInit, OnDestroy {
    private readonly destroy$: Subject<void> = new Subject<void>();
    private readonly MAX_ACTION_ITEMS: number = 3;

    readonly selectedItemsCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    readonly totalItemsCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    readonly actionItems$: ReplaySubject<ActionItem[]> = new ReplaySubject(1);

    isIndeterminate$!: Observable<boolean>;
    selectedItemsLabel$!: Observable<string>;

    selectAllFormControl: UntypedFormControl = new UntypedFormControl(false);

    @Input() set actionItems(actionItems: Observable<ActionItem>[]) {
        this.updateActionItems(actionItems);
    }

    @Input() darkMode = false;

    /**
     * Whether the bar carries a select-all checkbox of its own. It exists because the bar can *cover* the table's
     * header, hiding the checkbox it would otherwise stand in for; a bar that docks clear of the header has no
     * such gap to fill, and a second control for the same state cannot line up with the column either.
     */
    @Input() showSelectionCheckbox = true;

    @Input() set totalItemsCount(total: number) {
        this.totalItemsCount$.next(total);
    }

    @Input() set selectedItemsCount(selected: number) {
        this.selectedItemsCount$.next(selected);
    }

    @Input()
    allSelected?: boolean;

    @Output() shouldSelectAll: EventEmitter<boolean> = new EventEmitter<boolean>();

    constructor(private readonly translateService: TranslateService) {}

    ngOnInit(): void {
        this.initSelectedItemsLabelStream();
        this.initSelectionChangesListener();
        this.selectAllFormControlListener();

        combineLatest([this.isIndeterminate$, this.selectAllFormControl.valueChanges])
            .pipe(filter(([isIntermediate, selectedAll]) => !isIntermediate && !selectedAll))
            .subscribe(() => {
                this.deselectAll();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    deselectAll(): void {
        this.shouldSelectAll.emit(false);
    }

    private updateActionItems(actionItems: Observable<ActionItem>[]): void {
        combineLatest(actionItems)
            .pipe(takeUntil(this.destroy$))
            .subscribe(actionItems => {
                const overflowHandledActions: ActionItem[] = ReduceVisibleActionItems(
                    actionItems,
                    ActionItemStyle.PureSquareIconButtonInverted,
                    OpenDirection.AboveLeft
                );
                this.actionItems$.next(overflowHandledActions);
            });
    }

    private initSelectedItemsLabelStream(): void {
        this.selectedItemsLabel$ = this.selectedItemsCount$.pipe(
            map(itemsCount => {
                return this.translateService.instant('components.action-bar.select.items-selected', {
                    items: itemsCount
                });
            })
        );
    }

    private initSelectionChangesListener(): void {
        this.isIndeterminate$ = combineLatest([this.totalItemsCount$, this.selectedItemsCount$]).pipe(
            map(([total, selected]) => selected > 0 && selected < total)
        );

        combineLatest([this.totalItemsCount$, this.selectedItemsCount$]).subscribe(([total, selected]) =>
            this.updateSelectAllFormControlValue(total, selected)
        );
    }

    private updateSelectAllFormControlValue(totalItemsCount: number, selectedItemsCount: number): void {
        const areAllSelected = selectedItemsCount === totalItemsCount || this.allSelected!;
        this.selectAllFormControl.setValue(areAllSelected, { emitEvent: false });
    }

    private selectAllFormControlListener(): void {
        this.selectAllFormControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => this.onSelectAll(value));
    }

    private onSelectAll(shouldSelect: boolean): void {
        this.shouldSelectAll.emit(shouldSelect);
    }
}
